import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCase, useCaseSubmit } from "../../../context/CaseContext";
import { useTheme } from "../../../context/ThemeContext";
import { BargainingData, Configuration } from "../../../shared/CaseModel";
import { getSettledSummary } from "../../../shared/CaseModelUtility";
import { leafIssuesOfClaim } from "../../../shared/winChance";
import CenteredMessageBox from "../../general/CenteredMessageBox";
import Loading from "../../general/Loading";
import { Tabs } from "../../general/Tabs";
import { TonalBadge } from "../../general/TonalBadge";
import { BargainingClaim, BargainingParameters } from "./BargainingParameters";
import { BargainingRange } from "./BargainingRange";
import { extract } from "./Configuration";
import { SettledEmptyState } from "./SettledEmptyState";
import { judgmentOf } from "../../../shared/judgment";
import { JudgedState } from "../../timeline/JudgedState";

export const BargainingAnalytics = () => {
  const caseContext = useCase();
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });
  const isSmallDisplay = useMediaQuery(theme.breakpoints.down("md"));
  const submit = useCaseSubmit();

  const c = caseContext.getCase();
  const baseline = useMemo(() => extract(c), [c]);

  // Every hook must run before the guards below. Baseline outcomes arrive
  // asynchronously, so a guard placed above a hook changes the hook count
  // between renders and React tears the subtree down.
  const parties = caseContext.getPartyDescriptors();
  const baselineOutcomes = caseContext.getOutcomes();

  const submitVariant = async (newVariant: Configuration) => {
    newVariant.timestamp = Date.now();
    await submit(
      {
        variant: JSON.stringify(newVariant),
      },
      { method: "POST" },
    );
  };

  if (baselineOutcomes.length === 0) {
    return (
      <CenteredMessageBox message={t("analytics.settlement.noOutcomes")} />
    );
  }

  const bargainingConfiguration = c.variants?.bargaining;
  if (!bargainingConfiguration)
    return <CenteredMessageBox message={t("analytics.settlement.noData")} />;
  const bargainingResults = caseContext.getVariantResults(
    bargainingConfiguration.id,
  );
  if (!bargainingResults) {
    if (caseContext.variantResultsLoading) return <Loading />;
    return <CenteredMessageBox message={t("analytics.settlement.noData")} />;
  }

  const bargainingData: BargainingData[] =
    bargainingResults.getBargainingData() ?? [];

  // A settled claim leaves the model, so a fully settled case has no expected
  // values between the parties and therefore no ranges. That is a completed
  // outcome rather than missing data: state the agreed result and drop the
  // parameter controls, since there is nothing left to vary.
  if (judgmentOf(c)) return <JudgedState />;
  if (bargainingData.length === 0) {
    const settled = getSettledSummary(c);
    if (settled) return <SettledEmptyState caseId={c.id} {...settled} />;
    // empty for a reason we can't name — say so rather than render blank
    return <CenteredMessageBox message={t("analytics.settlement.noRanges")} />;
  }

  const nameOf = (partyId: string) => parties[partyId]?.name || partyId;

  const claims: BargainingClaim[] = (c.claims ?? []).map((claim) => ({
    id: claim.id,
    title: claim.title,
    // strip to id & name only
    claimant: { id: claim.claimant.id, name: claim.claimant.name },
    defendants:
      claim.defendants
        ?.filter((d) => c.parties?.some((p) => p.id === d.id))
        .map((d) => ({ id: d.id, name: d.name })) ?? [],
    leaves: leafIssuesOfClaim(claim).map(({ id, title }) => ({
      id,
      name: title,
    })),
  }));

  const tabs: { label: ReactNode; tabComponent: ReactNode }[] =
    bargainingData.map(({ partyId, opponents }) => ({
      label: (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <span>{nameOf(partyId)}</span>
          {parties[partyId]?.isClient && (
            <TonalBadge tone={theme.palette.primary}>
              {t("parties.clientBadge")}
            </TonalBadge>
          )}
        </Stack>
      ),
      tabComponent: (
        <Stack spacing={2.25}>
          {Object.keys(opponents).map((opponentId) => {
            const { baseline: baselineRanges, opponentRanges: variantRanges } =
              opponents[opponentId];
            return (
              <BargainingRange
                key={opponentId}
                name={nameOf(partyId)}
                opponentName={nameOf(opponentId)}
                baseline={baselineRanges}
                // without ranges of its own the variant is the baseline
                variant={variantRanges || baselineRanges}
              />
            );
          })}
        </Stack>
      ),
    }));

  return (
    <Stack
      spacing={2.25}
      sx={{
        userSelect: "none",
        // the ranges on the page are those of the variant before the change
        // until the new ones have arrived
        opacity: caseContext.variantResultsLoading ? 0.5 : 1,
        transition: "opacity 0.2s",
      }}
    >
      <Box>
        <Typography
          variant="h5"
          component="h1"
          sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
        >
          {t("analytics.settlement.bargaining.title")}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mt: 0.5, maxWidth: "76ch" }}
        >
          {t("analytics.settlement.bargaining.description")}
        </Typography>
      </Box>

      <BargainingParameters
        claims={claims}
        partyNames={Object.fromEntries(
          Object.entries(parties).map(([partyId, { name }]) => [partyId, name]),
        )}
        variant={bargainingConfiguration}
        baseline={baseline}
        onChange={submitVariant}
        compact={isSmallDisplay}
      />

      <Box>
        <Tabs tabs={tabs} />
      </Box>
    </Stack>
  );
};
